import { api } from './client'

export type SprintStatus = 'planned' | 'active' | 'closed'

export type Sprint = {
  id: number
  project_id: number
  name: string
  goal: string | null
  start_date: string | null
  end_date: string | null
  capacity_points: number | null
  status: SprintStatus
  /** Present on list responses only; null from show/create/update. */
  story_count: number | null
  total_points: number | null
  done_points: number | null
  created_at: string
  updated_at: string
}

/**
 * GET /v1/sprints — bare array (not paginated). project_id is required.
 *
 * Unlike `lookupSprints`, this carries capacity_points and the rollup counts,
 * so planning views don't have to fall back to a hardcoded capacity.
 */
export async function listSprints(
  projectId: number,
  opts: { status?: SprintStatus } = {},
): Promise<Sprint[]> {
  return api<Sprint[]>('/sprints', {
    query: { project_id: projectId, ...(opts.status ? { status: opts.status } : {}) },
  })
}

export type SprintCreate = {
  project_id: number
  name: string
  goal?: string
  start_date?: string
  end_date?: string
  capacity_points?: number
  status?: SprintStatus
}

export async function createSprint(data: SprintCreate): Promise<Sprint> {
  return api<Sprint>('/sprints', { method: 'POST', body: data })
}

/** An empty string clears goal, a date or capacity. */
export type SprintUpdate = Partial<Omit<SprintCreate, 'project_id' | 'capacity_points'>> & { capacity_points?: number | '' }

export async function updateSprint(id: number, patch: SprintUpdate): Promise<Sprint> {
  return api<Sprint>(`/sprints/${id}`, { method: 'PATCH', body: patch })
}

export async function deleteSprint(id: number): Promise<void> {
  return api<void>(`/sprints/${id}`, { method: 'DELETE' })
}

/** planned → active. 409 while another sprint in the project is active. */
export async function startSprint(id: number): Promise<Sprint> {
  return api<Sprint>(`/sprints/${id}/start`, { method: 'POST' })
}

export type CompleteSprint =
  | { move_to: 'backlog' }
  | { move_to: 'sprint'; target_sprint_id: number; start_next?: boolean }
  | {
      move_to: 'new'
      next: { name: string; goal?: string; start_date?: string; end_date?: string; capacity_points?: number }
      start_next?: boolean
    }

export type CompleteSprintResult = {
  closed: Sprint
  next: Sprint | null
  /** Keys of the unfinished stories that were moved. */
  moved: string[]
  moved_to: 'backlog' | 'sprint' | 'new'
}

/** End a sprint: close it and move its unfinished stories (one transaction). */
export async function completeSprint(id: number, body: CompleteSprint): Promise<CompleteSprintResult> {
  return api<CompleteSprintResult>(`/sprints/${id}/complete`, { method: 'POST', body })
}
